type ContentModuleCrit =
  | 'title'
  | 'description'
  | 'google_preview'
  | 'headings'
  | 'keywords_cloud'
  | 'images'
  | 'links_details'
  | 'language'
  | 'web_feeds';
type IndexingModuleCrit =
  | 'www_resolve'
  | 'robots_txt'
  | 'xml_sitemaps'
  | 'sitemaps_validity'
  | 'clean_url'
  | 'canonical_tags'
  | 'robots_tags'
  | 'noindex_nofollow'
  | 'hreflang'
  | 'broken_links'
  | 'underscores_url'
  | 'indexed_pages';
type MobileModuleCrit =
  | 'mobile_friendliness'
  | 'mobile_rendering'
  | 'mobile_taptargets'
  | 'mobile_compatibility'
  | 'mobile_fontsize'
  | 'mobile_viewport'
  | 'mobile_frameworks'
  | 'amp';
type StructuredDataModuleCrit =
  | 'schema_org'
  | 'open_graph'
  | 'twitter_card'
  | 'microformats';
type SecurityModuleCrit =
  | 'email_security'
  | 'dmarc'
  | 'ssl_secured'
  | 'mixed_content';
type PerformanceModuleCrit =
  | 'resources_minification'
  | 'resources_compression'
  | 'resources_cacheability'
  | 'image_optimization'
  | 'layout_shift_elements'
  | 'largest_contentful_paint_element';
type AccessibilityModuleCrit =
  | 'accessibility_contrast'
  | 'accessibility_navigation';
type TechnologiesModuleCrit =
  | 'pj_uptime'
  | 'ip_information'
  | 'technologies'
  | 'analytics_technologies'
  | 'doctype'
  | 'encoding';
type BrandingModuleCrit = 'url' | 'favicon' | 'custom_404';
type DomainModuleCrit =
  | 'domain_registration'
  | 'tld_cybersquating_prevention'
  | 'typo_cybersquating_prevention';
type BacklinksModuleCrit =
  | 'backlinks_score'
  | 'backlinks_counter'
  | 'backlinks_ref_domains'
  | 'pj_backlinks_quality';
type TrafficModuleCrit =
  | 'traffic_estimation'
  | 'traffic_popularity';
type LocalModuleCrit = 'local_directories' | 'local_online_reviews';
type SocialProfilesModuleCrit =
  | 'discovered_social_profiles'
  | 'facebook_brand_page'
  | 'twitter_account'
  | 'instagram'
  | 'linkedin'
  | 'crunchbase'
  | 'shared_count';
type SyncWithGscModuleCrit = 'gwt_queries';

export type Crit =
  | ContentModuleCrit
  | IndexingModuleCrit
  | MobileModuleCrit
  | StructuredDataModuleCrit
  | SecurityModuleCrit
  | PerformanceModuleCrit
  | AccessibilityModuleCrit
  | TechnologiesModuleCrit
  | BrandingModuleCrit
  | DomainModuleCrit
  | BacklinksModuleCrit
  | TrafficModuleCrit
  | LocalModuleCrit
  | SocialProfilesModuleCrit
  | SyncWithGscModuleCrit;

/* eslint-disable camelcase */
export type ModuleCrits = {
  content: ContentModuleCrit[];
  indexing: IndexingModuleCrit[];
  mobile: MobileModuleCrit[];
  structured_data: StructuredDataModuleCrit[];
  security: SecurityModuleCrit[];
  performance: PerformanceModuleCrit[];
  accessibility: AccessibilityModuleCrit[];
  technologies: TechnologiesModuleCrit[];
  branding: BrandingModuleCrit[];
  domain: DomainModuleCrit[];
  backlinks: BacklinksModuleCrit[];
  traffic: TrafficModuleCrit[];
  local: LocalModuleCrit[];
  social_profiles: SocialProfilesModuleCrit[];
  sync_with_gsc: SyncWithGscModuleCrit[];
};
/* eslint-enable camelcase */

export const MODULE_CRITS: ModuleCrits = {
  content: [
    'title',
    'description',
    'google_preview',
    'headings',
    'keywords_cloud',
    'images',
    'links_details',
    'language',
    'web_feeds'
  ],
  indexing: [
    'www_resolve',
    'robots_txt',
    'xml_sitemaps',
    'sitemaps_validity',
    'clean_url',
    'canonical_tags',
    'robots_tags',
    'noindex_nofollow',
    'hreflang',
    'broken_links',
    'underscores_url',
    'indexed_pages'
  ],
  mobile: [
    'mobile_friendliness',
    'mobile_rendering',
    'mobile_taptargets',
    'mobile_compatibility',
    'mobile_fontsize',
    'mobile_viewport',
    'mobile_frameworks',
    'amp'
  ],
  structured_data: ['schema_org', 'open_graph', 'twitter_card', 'microformats'],
  security: ['email_security', 'dmarc', 'ssl_secured', 'mixed_content'],
  performance: [
    'resources_minification',
    'resources_compression',
    'resources_cacheability',
    'image_optimization',
    'layout_shift_elements',
    'largest_contentful_paint_element'
  ],
  accessibility: ['accessibility_contrast', 'accessibility_navigation'],
  technologies: [
    'pj_uptime',
    'ip_information',
    'technologies',
    'analytics_technologies',
    'doctype',
    'encoding'
  ],
  branding: ['url', 'favicon', 'custom_404'],
  domain: [
    'domain_registration',
    'tld_cybersquating_prevention',
    'typo_cybersquating_prevention'
  ],
  backlinks: [
    'backlinks_score',
    'backlinks_counter',
    'backlinks_ref_domains',
    'pj_backlinks_quality'
  ],
  traffic: [
    'traffic_estimation',
    'traffic_popularity'
  ],
  local: ['local_directories', 'local_online_reviews'],
  social_profiles: [
    'discovered_social_profiles',
    'facebook_brand_page',
    'twitter_account',
    'instagram',
    'linkedin',
    'crunchbase',
    'shared_count'
  ],
  sync_with_gsc: ['gwt_queries']
};

// This interface needs to be merged with the same interface defined here: @hydra/types/review
/* eslint-disable camelcase */
export interface ReviewStructure {
  on_page: {
    content: ContentModuleCrit[]
    indexing: IndexingModuleCrit[]
    mobile: MobileModuleCrit[]
    structured_data: StructuredDataModuleCrit[]
    security: SecurityModuleCrit[]
    performance: PerformanceModuleCrit[]
    accessibility: AccessibilityModuleCrit[]
    technologies: TechnologiesModuleCrit[]
    branding: BrandingModuleCrit[]
    domain: DomainModuleCrit[]
  }
  off_page: {
    backlinks: BacklinksModuleCrit[]
    traffic: TrafficModuleCrit[]
    local: LocalModuleCrit[]
    social_profiles: SocialProfilesModuleCrit[]
    sync_with_gsc: SyncWithGscModuleCrit[]
  }
}
/* eslint-enable camelcase */

export const REVIEW_STRUCTURE: ReviewStructure = {
  on_page: {
    content: MODULE_CRITS.content,
    indexing: MODULE_CRITS.indexing,
    mobile: MODULE_CRITS.mobile,
    structured_data: MODULE_CRITS.structured_data,
    security: MODULE_CRITS.security,
    performance: MODULE_CRITS.performance,
    accessibility: MODULE_CRITS.accessibility,
    technologies: MODULE_CRITS.technologies,
    branding: MODULE_CRITS.branding,
    domain: MODULE_CRITS.domain
  },
  off_page: {
    backlinks: MODULE_CRITS.backlinks,
    traffic: MODULE_CRITS.traffic,
    local: MODULE_CRITS.local,
    social_profiles: MODULE_CRITS.social_profiles,
    sync_with_gsc: MODULE_CRITS.sync_with_gsc
  }
};
